import { useFeedback, useTranslation } from "@opendash/core";
import { Icon } from "@opendash/icons";
import { AdminToolbar } from "@opendash/ui";
import { Button, Tabs } from "antd";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useEffect, useState } from "react";
import { Kanban_State } from "../../../../js/parsegenerated";
import { KanbanCreateDrawer } from "./KanbanCreateDrawer";
import { KanbanStateForm } from "./KanbanStateForm";

export const ManageKanbanStates = observer(() => {
  const t = useTranslation();
  const { message } = useFeedback();
  const [columns, setColumns] = useState<Kanban_State[]>([]);
  const [selectedColumn, setSelectedColumn] = useState<Kanban_State>();
  const [loading, setLoading] = useState<boolean>();
  const [inboxDisabled, setInboxDisabled] = useState<boolean>(false);
  const [finishedDisabled, setFinishedDisabled] = useState<boolean>(false);
  const [createDrawerVisible, setCreateDrawerVisible] =
    useState<boolean>(false);
  const [reloadColumns, setReloadColumns] = useState(0);

  const onCancelCreate = () => {
    setCreateDrawerVisible(false);
  };

  const onOkDelete = () => {
    setReloadColumns(reloadColumns + 1);
  };

  const onInboxColumnChanged = () => {
    const inboxcolumns = columns.filter((column) => {
      return column.get("isInbox");
    });

    if (inboxcolumns.length <= 1) {
      setInboxDisabled(true);
    } else {
      setInboxDisabled(false);
    }
  };

  const onFinishedColumnChanged = () => {
    const finishedcolumns = columns.filter((column) => {
      return column.get("isFinished");
    });
    if (finishedcolumns.length <= 1) {
      setFinishedDisabled(true);
    } else {
      setFinishedDisabled(false);
    }
  };

  const onOkCreate = () => {
    setReloadColumns(reloadColumns + 1);
    setCreateDrawerVisible(false);
  };

  useEffect(() => {
    new Parse.Query(Kanban_State)
      .ascending("order")
      .equalTo("enabled", true)
      .find()
      .then((monitor_states) => {
        setColumns(monitor_states);
        setSelectedColumn(monitor_states[0]);

        const inboxcolumns = monitor_states.filter((monitor_state) => {
          return monitor_state.get("isInbox");
        });
        if (inboxcolumns.length <= 1) {
          setInboxDisabled(true);
        } else {
          setInboxDisabled(false);
        }

        const finishedcolumns = monitor_states.filter((monitor_state) => {
          return monitor_state.get("isFinished");
        });
        if (finishedcolumns.length <= 1) {
          setFinishedDisabled(true);
        } else {
          setFinishedDisabled(false);
        }
      });
  }, [reloadColumns]);

  return (
    <>
      <AdminToolbar
        title={t("maintenance:monitor.manage.headline")}
        actions={
          <Button
            type="primary"
            onClick={() => {
              setCreateDrawerVisible(true);
            }}
          >
            {t("maintenance:monitor.manage.add")}
          </Button>
        }
      />
      <div className="od-page-main">
        <Tabs
          style={{ marginBottom: 30 }}
          onTabClick={(key) => {
            setSelectedColumn(
              columns.find((column) => {
                return column.id === key;
              })
            );
          }}
          tabBarExtraContent={{
            left: (
              <>
                <Icon icon="fa:arrow-left"></Icon>
                <Button
                  type="text"
                  loading={loading}
                  onClick={async () => {
                    if (!selectedColumn) {
                      return;
                    }
                    // Move to left is only allowed if the position is not 0. Negative numbers are not allowed.
                    if (selectedColumn.get("order") <= 0) {
                      return;
                    }
                    setLoading(true);

                    const ind = columns.findIndex((col) => {
                      return selectedColumn.id === col.id;
                    });

                    // Set new order for column on i-1
                    const previousColumn = columns[ind - 1];
                    if (previousColumn) {
                      previousColumn.set("order", selectedColumn.get("order"));
                      await previousColumn.save();
                    }

                    // Start moving the column and set new order
                    selectedColumn.set(
                      "order",
                      selectedColumn.get("order") - 1
                    );
                    selectedColumn.save().then(
                      (savedobject) => {
                        columns.sort((a, b) => {
                          if (a.get("order") < b.get("order")) {
                            return -1;
                          }
                          if (a.get("order") > b.get("order")) {
                            return 1;
                          }
                          return 0;
                        });
                        setLoading(false);
                        message.success(
                          t("maintenance:global.messages.save.success"),
                          2
                        );
                      },
                      (error) => {
                        console.log(error);
                        message.error(
                          t("maintenance:global.messages.save.error"),
                          2
                        );
                      }
                    );
                  }}
                >
                  {t("maintenance:monitor.manage.moveleft")}
                </Button>
              </>
            ),
            right: (
              <>
                <Button
                  type="text"
                  onClick={async () => {
                    if (!selectedColumn) {
                      return;
                    }
                    setLoading(true);

                    const ind = columns.findIndex((col) => {
                      return selectedColumn.id === col.id;
                    });

                    // Set new order for column on i-1
                    const previousColumn = columns[ind + 1];
                    if (previousColumn) {
                      previousColumn.set("order", selectedColumn.get("order"));
                      await previousColumn.save();
                    }

                    // Start moving the column and set new order
                    selectedColumn.set(
                      "order",
                      selectedColumn.get("order") + 1
                    );
                    selectedColumn.save().then(
                      (savedobject) => {
                        columns.sort((a, b) => {
                          if (a.get("order") < b.get("order")) {
                            return -1;
                          }
                          if (a.get("order") > b.get("order")) {
                            return 1;
                          }
                          return 0;
                        });
                        setLoading(false);
                        message.success(
                          t("maintenance:global.messages.save.success"),
                          2
                        );
                      },
                      (error) => {
                        console.log(error);
                        message.error(
                          t("maintenance:global.messages.save.error"),
                          2
                        );
                      }
                    );
                  }}
                >
                  {t("maintenance:monitor.manage.moveright")}
                </Button>
                <Icon icon="fa:arrow-right"></Icon>
              </>
            ),
          }}
          items={columns.map((column) => {
            return {
              key: column.id,
              label: column.get("label"),
              children: (
                <KanbanStateForm
                  monitorstate={column}
                  inboxdisabled={inboxDisabled}
                  finisheddisabled={finishedDisabled}
                  onFinishedColumnChanged={onFinishedColumnChanged}
                  onInboxColumnChanged={onInboxColumnChanged}
                  onOkDelete={onOkDelete}
                />
              ),
            };
          })}
        />

        <KanbanCreateDrawer
          open={createDrawerVisible}
          onOk={onOkCreate}
          onCancel={onCancelCreate}
        />
      </div>
    </>
  );
});
